import {
  AtLeast,
  Block,
  BlockType,
  ComponentBlock,
  DocumentBlock,
  InstanceBlock,
  ListBlock,
  blockTypesRecord as BT,
  isComponentBlock,
  isDocumentBlock,
  isInstanceBlock,
  isListBlock,
} from '..'
import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import {
  getApiNameWithoutCollisions,
  getApiSlugWithoutCollisions,
} from '../../api-kv'
import { invariant } from '@epic-web/invariant'
import {
  makeInstanceChildBlock,
  type LiteBlock,
} from '../schema/block/instance'

// ----------------------------------------------------------------------------------------------------------
//                                                  🆘 Helpers
// ----------------------------------------------------------------------------------------------------------

export type PreviewLinkBlock =
  | InstanceBlock
  | ListBlock
  | DocumentBlock
  | ComponentBlock

// sort blocks with apiName first and without apiName last
export const sortChildrenByApiNameFirst = (
  a: Block & { apiName?: string },
  b: Block & { apiName?: string }
) => {
  return (a.apiName ? 0 : 1) - (b.apiName ? 0 : 1)
}

export const isValidBlockPreviewType = (
  block: Block
): block is DocumentBlock | ComponentBlock | InstanceBlock | ListBlock => {
  return (
    isDocumentBlock(block) ||
    isComponentBlock(block) ||
    isInstanceBlock(block) ||
    isListBlock(block)
  )
}

export const isValidBlockPreviewType_string = (type: BlockType) => {
  return (
    type === 'document' ||
    type === 'component' ||
    type === 'instance' ||
    type === 'list'
  )
}

export const sortChildrenByApiNameAndSlug = (
  a: AtLeast<Block, 'apiName' | 'slug'>,
  b: AtLeast<Block, 'apiName' | 'slug'>
) => {
  // sort the items wich has slug then apiName first
  return `${a.slug ?? ''}${a.apiName ?? ''}`.localeCompare(
    `${b.slug ?? ''}${b.apiName ?? ''}`
  )
}

// Helper function to get component's API name, recursively if needed
export const getComponentApiName = (
  componentId: string,
  treeManager: AtLeast<
    TreeManager<LiteBlock, NonNullable<unknown>>,
    'getChildrenKeys' | 'getNode'
  >,
  seenIds = new Set<string>()
): string | undefined => {
  // Prevent infinite recursion
  if (seenIds.has(componentId)) {
    return undefined
  }
  seenIds.add(componentId)

  const componentNode = treeManager.getNode(componentId)
  if (!componentNode?.value) {
    return undefined
  }

  // If component has an API name, use it
  if (componentNode.value.apiName) {
    return componentNode.value.apiName
  }
  // when no found parentKey
  if (!componentNode.parentKey) {
    return getApiNameWithoutCollisions(
      componentNode.value.sourceBlockId ?? componentNode.value.id,
      componentNode.value.title,
      new Set()
    )
  }

  // If not, we need to generate one by looking at its siblings
  const componentParent = treeManager.getNode(componentNode.parentKey)
  if (!componentParent) {
    return undefined
  }

  const siblings =
    treeManager.getChildrenKeys(componentParent.key)?.map((cId) => {
      const c = treeManager.getNode(cId, { required: true }).value
      return c
    }) ?? []

  // Collect existing API names from siblings
  const siblingApiNames = new Set<string>()
  siblings.forEach((sibling) => {
    if (sibling.apiName) {
      siblingApiNames.add(sibling.apiName)
    }
  })

  // Generate API name for the component
  return getApiNameWithoutCollisions(
    componentNode.value.sourceBlockId ?? componentNode.value.id,
    componentNode.value.title,
    siblingApiNames
  )
}

export const getPreviewLink = (
  blockEntry: TreeNode<PreviewLinkBlock>,
  blockTreeManager: TreeManager<Block, NonNullable<unknown>>
): [string, Block] => {
  // we have 3 cases
  // 1. block has a preview url and use his own variables
  // 2. block has no preview url but inherit the near preview url and use his own variables (instance of list block)
  // 3. block has no preview url and inherit the near preview url and use the near variables
  const block = blockEntry.value
  const parentNode = blockEntry.parentKey
    ? (blockTreeManager.getNode(blockEntry.parentKey) as
        | TreeNode<PreviewLinkBlock>
        | undefined)
    : null
  const parent = parentNode?.value

  if (block.previewUrl) {
    return [block.previewUrl, block]
  }
  if (parent && isListBlock(parent) && isInstanceBlock(block)) {
    const templateBlock = blockTreeManager.getNode(
      block.typeOptions.targetBlockId
    )?.value as PreviewLinkBlock | undefined
    if (!templateBlock?.previewUrl) {
      return getPreviewLink(parentNode, blockTreeManager)
    }
    if (templateBlock.previewUrl) {
      return [templateBlock?.previewUrl ?? '', block]
    }
  }
  if (parent && parent.previewUrl) {
    return [parent.previewUrl, parent]
  }
  if (parent && !parent.previewUrl) {
    return getPreviewLink(parentNode, blockTreeManager)
  }
  return ['', block]
}

const varPattern = /\{([^{}]+)\}/g

export function replaceVars(
  inputString: string,
  data: null | Record<string, string | undefined | null>
): string {
  const replacedString = inputString.replace(varPattern, (match, key) => {
    const trimmedKey = key.trim()
    const trimmedKeyWithBrackets = `{${trimmedKey}}`
    const value =
      data?.[trimmedKey] ?? data?.[trimmedKeyWithBrackets] ?? undefined
    return value !== undefined ? value : ''
  })

  return replacedString
}

export function isVar(inputString: string) {
  return varPattern.test(inputString)
}

export const getPreviewLinkV2 = (
  blockEntry: TreeNode<LiteBlock>,
  blockTreeManager: TreeManager<LiteBlock>
): [string, TreeNode<LiteBlock>] => {
  // we have 3 cases
  // 1. block has a preview url and use his own variables
  // 2. block has no preview url but inherit the near preview url and use his own variables (instance of list block)
  // 3. block has no preview url and inherit the near preview url and use the near variables
  const block = blockEntry.value
  const parentNode = blockEntry.parentKey
    ? (blockTreeManager.getNode(blockEntry.parentKey) as
        | TreeNode<PreviewLinkBlock>
        | undefined)
    : null
  const parent = parentNode?.value

  if ('previewUrl' in block && block.previewUrl) {
    return [block.previewUrl, blockEntry]
  }
  if (parent && isListBlock(parent) && block.type === 'instance') {
    const templateBlock = blockTreeManager.getNode(
      block.typeOptions.targetBlockId
    )?.value as PreviewLinkBlock | undefined
    if (!templateBlock?.previewUrl) {
      return getPreviewLinkV2(parentNode, blockTreeManager)
    }
    if (templateBlock.previewUrl) {
      return [templateBlock?.previewUrl ?? '', blockEntry]
    }
  }
  if (parent && parent.previewUrl) {
    return [parent.previewUrl, parentNode]
  }
  if (parent && !parent.previewUrl) {
    return getPreviewLinkV2(parentNode, blockTreeManager)
  }
  return ['', blockEntry]
}

/** IMPORTANT: During runtime, use under `useTreeSlice` */
export const getApiNameAndSlug = ({
  nodeBlock,
  treeManager,
}: {
  nodeBlock: TreeNode<LiteBlock>
  treeManager: AtLeast<
    TreeManager<LiteBlock, NonNullable<unknown>>,
    'getChildrenKeys' | 'getNode'
  >
}) => {
  // iterate from the parent to get the siblings and generate the apiName and slug
  const parent = treeManager.getNode(nodeBlock.parentKey ?? '')
  if (!parent) return {}

  const children =
    treeManager.getChildrenKeys(parent.key)?.map((cId) => {
      const c = treeManager.getNode(cId, { required: true }).value
      if (parent.value.type === 'instance') {
        invariant(c.instanceChildTarget, 'instanceChildTarget not found')
        return makeInstanceChildBlock({
          block: c,
          parentSourceBlockId: parent.value.sourceBlockId || parent.value.id,
          targetBlock: treeManager.getNode(c.instanceChildTarget, {
            required: true,
          }).value,
        })
      } else return c
    }) ?? []

  const generatedApiNames = new Set<string>()
  const generatedSlugs = new Set<string>()
  const result = new Map<string, [string, string]>()

  // First loop: Collect all existing API names and slugs from children
  children.forEach((child) => {
    // Collect existing API names
    if (child.apiName) {
      generatedApiNames.add(child.apiName)
    }

    // Collect existing slugs
    if ('slug' in child && child.slug) {
      generatedSlugs.add(child.slug)
    }
  })

  // Second loop: Generate API names and slugs for all children
  const componentApiNames = new Map<string, string>()
  children.forEach((child) => {
    let apiName: string

    if (
      child.type === 'instance' &&
      [BT.list, BT.richText].includes(parent.value.type)
    ) {
      // For instances under list/richText, we MUST inherit the target component's API name
      if ('typeOptions' in child && child.typeOptions?.targetBlockId) {
        // Try to get the target component's API name

        const targetApiName =
          componentApiNames.get(child.typeOptions.targetBlockId) ??
          getComponentApiName(child.typeOptions.targetBlockId, treeManager)
        if (targetApiName) {
          apiName = targetApiName
          componentApiNames.set(child.typeOptions.targetBlockId, apiName)
        } else {
          // If we can't get the target's API name, generate one for this instance
          apiName = getApiNameWithoutCollisions(
            child.sourceBlockId ?? child.id,
            child.title,
            generatedApiNames
          )
        }
      } else {
        // Instance without target - generate new API name
        apiName = getApiNameWithoutCollisions(
          child.sourceBlockId ?? child.id,
          child.title,
          generatedApiNames
        )
      }
    } else {
      // Handle components and other block types
      if (child.apiName) {
        apiName = child.apiName
      } else {
        apiName = getApiNameWithoutCollisions(
          child.sourceBlockId ?? child.id,
          child.title,
          generatedApiNames
        )
      }
    }

    generatedApiNames.add(apiName)

    // Generate slug
    const slug =
      child.slug ??
      getApiSlugWithoutCollisions(
        child.sourceBlockId ?? child.id,
        child.title,
        generatedSlugs
      )
    generatedSlugs.add(slug)

    // Store result
    result.set(child.id, [apiName, slug])
  })

  // Find the result for the requested node
  const nodeResult = result.get(nodeBlock.value.id)
  if (!nodeResult) {
    throw new Error(`Node result not found for block ${nodeBlock.value.id}`)
  }

  return {
    _apiName: nodeResult[0],
    _slug: nodeResult[1],
    result,
  }
}
